<template>
    <Teleport to="body">
        <Transition name="modal-fade">
            <div v-if="modelValue && template" class="modal-wrapper-params">
                <div class="modal-backdrop-params" @click="fechar"></div>

                <div class="modal-card-params shadow-premium" role="dialog" aria-modal="true">
                    <div class="px-3 py-3 border-bottom d-flex justify-content-between align-items-center">
                        <div>
                            <h6 class="fw-bold mb-0">Preencher variáveis do template</h6>
                            <p class="text-muted font-size-12 mb-0">{{ template.displayName }}</p>
                        </div>
                        <button type="button" class="btn-close-params" @click="fechar"><i class="ri-close-line"></i></button>
                    </div>

                    <div class="px-3 py-3 corpo-params custom-scrollbar-light">
                        <!-- A Meta conta os parâmetros: mandar menos do que o template declara é
                             recusa certa (132000), e ela acontece DEPOIS do envio parecer ter dado
                             certo. Por isso o campo é obrigatório aqui. -->
                        <p class="text-muted font-size-12 mb-3">
                            Este template exige um valor por variável. Use <code>{nome_contato}</code> ou
                            <code>{nome_whatsapp}</code> para inserir o nome do contato no envio.
                        </p>

                        <div v-for="f in campos" :key="f.section + ':' + f.key" class="mb-2">
                            <label class="font-size-12 text-muted mb-1 d-block">{{ f.label }}</label>
                            <!-- O placeholder mostra o EXEMPLO aprovado na Meta para esta
                                 variável: "Vanderlúcia" diz o que se espera ali, coisa que
                                 um "Valor ou variável" genérico nunca disse. -->
                            <input type="text" class="form-control form-control-sm" :placeholder="exemploDoCampo(f)"
                                :value="valores[f.section][f.key] ?? ''"
                                @input="onInput(f.section, f.key, $event)"
                                @keyup.enter="confirmar">
                        </div>

                        <!-- Pré-visualização colapsável: a mesma bolha da tela de cadastro,
                             mas refletindo o que está sendo digitado. Fechada por padrão para
                             não empurrar os campos para fora da área visível. -->
                        <div class="mt-3 border-top pt-3">
                            <button type="button" class="btn-preview-toggle" @click="previewAberta = !previewAberta">
                                <i class="ri-eye-line me-1"></i>
                                <span>Pré-visualizar mensagem</span>
                                <i class="ri-arrow-down-s-line ms-auto icon-transition" :class="{ 'rotate-180': previewAberta }"></i>
                            </button>

                            <transition name="collapse">
                                <div v-show="previewAberta" class="preview-area custom-chat-bg mt-2">
                                    <MetaTemplatePreview
                                        :headerType="previewDados.headerType"
                                        :headerText="previewDados.headerText"
                                        :bodyText="previewDados.bodyText"
                                        :footerText="previewDados.footerText"
                                        :buttons="previewDados.buttons"
                                    />
                                    <p class="text-muted font-size-11 mb-0 mt-2 text-center">
                                        Campo vazio aparece com o valor de exemplo do template.
                                    </p>
                                </div>
                            </transition>
                        </div>

                        <div v-if="variaveisAutomaticas.length" class="mt-3">
                            <label class="label-whiz d-block mb-1">Variáveis automáticas</label>
                            <p class="text-muted font-size-11 mb-0">
                                {{ variaveisAutomaticas.map(v => v.label).join(', ') }} — preenchidas pelo sistema no envio.
                            </p>
                        </div>
                    </div>

                    <div class="px-3 py-2 border-top d-flex justify-content-end gap-2">
                        <button type="button" class="btn btn-sm btn-light rounded-pill px-3" @click="fechar">Cancelar</button>
                        <button type="button" class="btn btn-sm btn-primary rounded-pill px-3"
                            :disabled="!valido || enviando" @click="confirmar">
                            <span v-if="enviando" class="spinner-border spinner-border-sm me-1"></span>
                            Enviar template
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup lang="ts">
import { computed, reactive, ref, watch } from 'vue';
import { IMetaParams, IMetaTemplate } from '@/services/templates-wpp-meta/meta-wpp-template.service';
import {
    aplicarParametros,
    buildMetaParams,
    exemploDoCampo,
    parametrosDoTemplate,
    parseComponentJson,
    SecaoParametro,
} from '@/services/templates-wpp-meta/meta-template-params';
import MetaTemplatePreview from '@/components/forms/MetaTemplatePreview.vue';
import { MetaButtonVariants } from '@/services/templates-wpp-meta/template-buttons';
import {
    extractSupportedVariables,
    templateVariableDef,
} from '@/services/templates-wpp-meta/meta-template-variables';

/**
 * Coleta os valores das variáveis de UM template já escolhido.
 *
 * O `MetaTemplatePicker` faz isto embutido na escolha do template, o que serve ao
 * agendamento e ao disparo; o envio pelo chat já sabe qual é o template e só precisa
 * desta metade. As regras de quais variáveis pedir são as mesmas dos dois lados
 * (`meta-template-params`).
 */
const props = defineProps<{
    modelValue: boolean;
    template: IMetaTemplate | null;
    /** Mantém o botão em estado de envio enquanto o pai chama a API. */
    enviando?: boolean;
}>();

const emit = defineEmits<{
    (e: 'update:modelValue', valor: boolean): void;
    (e: 'confirmar', params: IMetaParams): void;
}>();

const valores = reactive<{ header: Record<string, string>; body: Record<string, string> }>({ header: {}, body: {} });

const parametros = computed(() => parametrosDoTemplate(props.template));
const campos = computed(() => parametros.value.fields);
const valido = computed(() => campos.value.every(f => (valores[f.section][f.key] ?? '').trim().length > 0));

/** Informativas: quem as preenche é o servidor, no envio. */
const variaveisAutomaticas = computed(() => {
    const t = props.template;
    if (!t) return [];
    const nomes = [
        ...extractSupportedVariables(parseComponentJson(t.headerJson)?.text as string | undefined),
        ...extractSupportedVariables(parseComponentJson(t.bodyJson)?.text as string | undefined),
    ];
    return [...new Set(nomes)].map(templateVariableDef);
});

const previewAberta = ref(false);

/**
 * O que a bolha mostra: o valor DIGITADO quando existe, o exemplo do template
 * quando o campo ainda está vazio. É o que torna a pré-visualização útil no meio
 * do preenchimento — mostrar `{{...}}` cru aqui não ajudaria ninguém.
 */
const previewDados = computed(() => {
    const t = props.template;
    const header = parseComponentJson(t?.headerJson);
    const body = parseComponentJson(t?.bodyJson);
    const footer = parseComponentJson(t?.footerJson);
    const botoes = parseComponentJson(t?.buttonsJson);

    const comExemplos = (secao: SecaoParametro): Record<string, string> => {
        const mapa: Record<string, string> = {};
        for (const campo of campos.value) {
            if (campo.section !== secao) continue;
            mapa[campo.key] = (valores[secao][campo.key] ?? '').trim() || exemploDoCampo(campo);
        }
        return mapa;
    };

    const paramsPreview = {
        header: Object.entries(comExemplos('header')) as [string, string][],
        body: Object.entries(comExemplos('body')) as [string, string][],
    } as IMetaParams;

    const temCabecalhoTexto = !!t?.headerType?.includes('TEXT');

    return {
        headerType: temCabecalhoTexto ? ('text' as const) : null,
        headerText: aplicarParametros(header?.text as string | undefined, paramsPreview, 'header'),
        bodyText: aplicarParametros(body?.text as string | undefined, paramsPreview, 'body'),
        footerText: (footer?.text as string | undefined) ?? '',
        buttons: (((botoes?.buttons as { type?: string; text?: string }[] | undefined) ?? []).map(b => {
            const variante = MetaButtonVariants.find(v => v.kind === b.type);
            return {
                icon: variante?.icon || 'ri-checkbox-blank-circle-line',
                label: b.text?.trim() || variante?.label || 'Botão',
            };
        })),
    };
});

const onInput = (secao: SecaoParametro, chave: string, ev: Event) => {
    valores[secao][chave] = (ev.target as HTMLInputElement).value;
};

const fechar = () => emit('update:modelValue', false);

const confirmar = () => {
    if (!valido.value || props.enviando) return;
    emit('confirmar', buildMetaParams(parametros.value, valores));
};

// Abrir com valores de um envio anterior mandaria o texto errado sem ninguém notar.
watch(() => [props.modelValue, props.template?.id], () => {
    valores.header = {};
    valores.body = {};
    previewAberta.value = false;
});
</script>

<style scoped>
.modal-wrapper-params {
    position: fixed; inset: 0; z-index: 1080;
    display: flex; align-items: center; justify-content: center; padding: 16px;
}
.modal-backdrop-params { position: absolute; inset: 0; background: rgba(15, 23, 42, .45); backdrop-filter: blur(2px); }
.modal-card-params {
    position: relative; z-index: 1; width: 100%; max-width: 460px;
    background: var(--bs-body-bg, #fff); border-radius: 14px; overflow: hidden;
    display: flex; flex-direction: column; max-height: 85vh;
}
.corpo-params { overflow-y: auto; }
.btn-close-params {
    border: none; background: transparent; color: #64748b; font-size: 20px; line-height: 1; cursor: pointer;
}
.modal-fade-enter-active, .modal-fade-leave-active { transition: opacity .15s ease; }
.modal-fade-enter-from, .modal-fade-leave-to { opacity: 0; }

.btn-preview-toggle {
    display: flex; align-items: center; width: 100%;
    border: none; background: transparent; padding: 4px 0;
    font-size: 12px; font-weight: 600; color: var(--bs-body-color, #334155); cursor: pointer;
}
.preview-area { border-radius: 10px; padding: 12px; }
.icon-transition { transition: transform .3s ease; }
.rotate-180 { transform: rotate(180deg); }

/* Mesmo colapso de `ModalPermissoesUsuario.vue` — o repo não tem componente genérico. */
.collapse-enter-active, .collapse-leave-active {
    transition: all .3s cubic-bezier(.4, 0, .2, 1); overflow: hidden; max-height: 800px; opacity: 1;
}
.collapse-enter-from, .collapse-leave-to { max-height: 0; opacity: 0; }
</style>
